/**
 * Čista logika usklađivanja monitor-cachea kod promjene stanja instance (pauza/nastavak/...).
 *
 * KLJUČNO: timer na monitoringu (TableTimeLeft) odbrojava po AUTORITATIVNOM `sync`
 * snapshotu (isRunning / endsAt), a NE po `state`. Ako se optimistički promijeni samo
 * `state`, `sync` ostane nekonzistentan, pa monitor:
 *   - na PAUZI i dalje odbrojava (sync.isRunning je još true), a
 *   - na NASTAVKU ostane zamrznut (sync.isRunning je još false).
 *
 * Zato ovdje `sync` uskladimo sa novim stanjem:
 *   - InProgress -> sync.isRunning = true (monitor odmah odbrojava od zadnjeg
 *     poznatog `remainingSeconds` dok ne stigne precizni `endsAt` iz refetch-a),
 *   - bilo koje drugo stanje (Pauza / Excluded / ...) -> sync.isRunning = false,
 *     endsAtUnixMs = null (zamrznuto).
 *
 * Nakon ove optimističke promjene UVIJEK slijedi autoritativni refetch koji donosi
 * precizni (deterministički) `endsAt`, pa se prikaz fino kalibrira.
 */

/** ExamPeriodMonitorState.InProgress (=4). Drži usklađeno s enumom u types/exam/ExamPeriodMonitor.ts. */
export const MONITOR_STATE_IN_PROGRESS = 4;

interface SyncLike {
  isRunning: boolean;
  endsAtUnixMs: number | null;
  [key: string]: unknown;
}

interface MonitorItemLike {
  id: string;
  state: number;
  sync?: SyncLike | null;
  [key: string]: unknown;
}

/**
 * Vraća novi niz s usklađenim `state` I `sync` za instance u `ids`. Ostale stavke
 * ostaju netaknute. Nedostajući `sync` (stariji backend) ostaje nedefiniran.
 */
export function applyMonitorStateChange<T extends MonitorItemLike>(
  data: T[] | undefined,
  ids: string[],
  newState: number
): T[] | undefined {
  if (!data) return data;

  const isRunning = newState === MONITOR_STATE_IN_PROGRESS;

  return data.map((item) => {
    if (!ids.includes(item.id)) {
      return item;
    }

    let sync = item.sync;
    if (sync) {
      sync = isRunning
        ? { ...sync, isRunning: true }
        : { ...sync, isRunning: false, endsAtUnixMs: null };
    }

    return { ...item, state: newState, sync };
  });
}
